/** FmDashboardOverview — sealed owner-dashboard archetype. */
import React from 'react';
import { MessageOutlined } from '@ant-design/icons';
import { FmBanner } from './FmBanner';
import { FmButton } from './FmButton';
import { FmRoutePage, FmStack, FmText } from './FmComposition';
import { FmEntityCardGrid, type EntityCardDescriptor } from './FmEntityCardGrid';
import { FmEvidence } from './FmEvidence';
import { FmActionGroup } from './FmListPrimitives';
import { FmSectionBand } from './FmReportComposition';
import { FmTable } from './FmTable';
import type {
  FmDashboardActionDescriptor,
  FmDashboardAssistantAction,
  FmDashboardCalmAnswerSection,
  FmDashboardHandlers,
  FmDashboardOverviewProps,
  FmDashboardRankedCollectionSection,
  FmDashboardRankedRecord,
  FmDashboardState,
} from './FmDashboardOverview.types';
import { formatDashboardScalar, tableColumns } from './FmDashboardOverview.expertProjection';
import { validateFmDashboardOverviewProps } from './FmDashboardOverview.validation';
import './FmDashboardOverview.css';
import { DashboardHeader } from './FmDashboardOverview.header';

export type * from './FmDashboardOverview.types';

function invokeAssistant(action: FmDashboardAssistantAction, handlers: FmDashboardHandlers): void {
  if (action.tool === 'explain') handlers.assistantTools.explain?.(action.context);
  if (action.tool === 'navigate') handlers.assistantTools.navigate?.(action.context);
}

function invokePrimary(
  action: FmDashboardActionDescriptor,
  handlers: FmDashboardHandlers,
  record?: FmDashboardRankedRecord,
): void {
  if (action.pending) return;
  handlers.onPrimaryAction({
    actionId: action.id,
    jobId: action.jobId,
    ...(record
      ? {
          recordId: record.id,
          entityId: record.entityId,
          propertyId: record.propertyId,
        }
      : {}),
  });
}

function stateAction(state: FmDashboardState): FmDashboardActionDescriptor | undefined {
  switch (state.status) {
    case 'empty':
    case 'locked':
      return state.action;
    case 'error':
      return state.retry;
    default:
      return undefined;
  }
}

/** A partial or stale notice sits beside the cards it qualifies; empty replaces them. */
function stateNoticeSize(status: FmDashboardState['status']): 'default' | 'lead' | 'inline' {
  if (status === 'empty') return 'lead';
  return status === 'partial' || status === 'stale' ? 'inline' : 'default';
}

const StateNotice: React.FC<{
  state: FmDashboardState;
  handlers: FmDashboardHandlers;
  testId: string;
  renderAction?: boolean;
}> = ({ state, handlers, testId, renderAction = true }) => {
  if (state.status === 'ready') return null;
  const action = stateAction(state);
  return (
    <FmBanner
      status={state.status}
      size={stateNoticeSize(state.status)}
      title={state.label}
      description={state.detail !== state.label ? state.detail : undefined}
      timestamp={state.status === 'stale' ? state.updatedAt : undefined}
      action={
        action && renderAction ? (
          <FmButton
            variant={state.status === 'empty' ? 'primary' : 'bordered'}
            loading={action.pending}
            onClick={() => invokePrimary(action, handlers)}
          >
            {action.label}
          </FmButton>
        ) : undefined
      }
      data-testid={`${testId}-state-${state.status}`}
    />
  );
};

function sharesStatePresentation(left: FmDashboardState, right: FmDashboardState): boolean {
  if (left.status !== right.status) return false;
  if (left.status === 'ready' || right.status === 'ready') return true;
  return (
    left.label === right.label &&
    left.detail === right.detail &&
    (left.status !== 'stale' || right.status !== 'stale' || left.updatedAt === right.updatedAt)
  );
}

const CalmAnswer: React.FC<{
  section: FmDashboardCalmAnswerSection;
  topRecord?: FmDashboardRankedRecord;
  handlers: FmDashboardHandlers;
  testId: string;
}> = ({ section, topRecord, handlers, testId }) => {
  const assistantAction = section.assistantAction;
  const showTopAction = Boolean(topRecord) && section.state.status !== 'locked';
  // One filled action per region: the top record's job owns it when shown, and a
  // primary-intent utility then renders as a secondary button beside it.
  const showUtilities =
    section.state.status !== 'locked' &&
    Boolean(section.utilityActions) &&
    Boolean(section.utilityActionGroupLabel);
  return (
    <FmSectionBand
      id={section.id}
      title={section.ownerQuestion}
      tone="plain"
      density="compact"
      className="fm-dashboard-overview__answer-band"
      data-testid={`${testId}-section-${section.id}`}
    >
      <FmStack gap="sm">
        <div className="fm-dashboard-overview__answer-main">
          <FmText
            as="p"
            weight="bold"
            className="fm-dashboard-overview__literal-answer"
            data-testid={`${testId}-literal-answer`}
          >
            {section.literalAnswer}
          </FmText>
          {showTopAction || assistantAction || showUtilities ? (
            <FmActionGroup collapseOnMobile data-testid={`${testId}-answer-actions`}>
              {showTopAction && topRecord ? (
                <FmButton
                  variant="primary"
                  onClick={() => invokePrimary(topRecord.primaryAction, handlers, topRecord)}
                  data-testid={`${testId}-answer-primary`}
                >
                  {topRecord.primaryAction.label}
                </FmButton>
              ) : null}
              {assistantAction ? (
                <FmButton
                  variant="text"
                  leadingIcon={<MessageOutlined />}
                  onClick={() => invokeAssistant(assistantAction, handlers)}
                  data-testid={`${testId}-answer-assistant`}
                >
                  {assistantAction.label}
                </FmButton>
              ) : null}
              {showUtilities && section.utilityActions && section.utilityActionGroupLabel ? (
                <fieldset
                  className="fm-dashboard-overview__utilities"
                  aria-label={section.utilityActionGroupLabel}
                >
                  <FmActionGroup collapseOnMobile data-testid={`${testId}-answer-utilities`}>
                    {section.utilityActions.map((action) => (
                      <FmButton
                        key={action.id}
                        loading={action.pending}
                        variant={
                          action.intent === 'primary' && !showTopAction ? 'primary' : 'default'
                        }
                        onClick={() => invokePrimary(action, handlers)}
                        data-testid={`${testId}-utility-${action.id}`}
                      >
                        {action.label}
                      </FmButton>
                    ))}
                  </FmActionGroup>
                </fieldset>
              ) : null}
            </FmActionGroup>
          ) : null}
        </div>
        <StateNotice state={section.state} handlers={handlers} testId={`${testId}-${section.id}`} />
        {section.evidence ? (
          <FmEvidence
            evidence={section.evidence}
            data-testid={`${testId}-${section.id}-evidence`}
          />
        ) : null}
      </FmStack>
    </FmSectionBand>
  );
};

function recordNote(record: FmDashboardRankedRecord): string {
  if (!('changeSummary' in record) || record.changeSummary === record.reason) {
    return record.reason;
  }
  return `${record.reason} ${record.changeSummary}`;
}

function toEntityCard(
  record: FmDashboardRankedRecord,
  handlers: FmDashboardHandlers,
  testId: string,
  showRank: boolean,
  readOnly: boolean,
): EntityCardDescriptor {
  const [leadField, ...remainingFields] = record.summaryFields;
  const { detailsAction, removeAction, secondaryAction } = record;
  return {
    id: record.id,
    title: record.title,
    titleAction:
      !readOnly && detailsAction
        ? {
            id: detailsAction.id,
            label: detailsAction.label,
            onClick: () => invokePrimary(detailsAction, handlers, record),
            testId: `${testId}-details-${record.id}`,
          }
        : undefined,
    mediaContext: record.mediaContext,
    propertyMap: record.propertyMap
      ? {
          ...record.propertyMap,
          onVisibilityChange: (visible) =>
            handlers.onMapVisibilityChange?.(record.propertyId, visible),
        }
      : undefined,
    mediaCueLabels: record.mediaCueLabels,
    statusChip: {
      label: record.status.label,
      variant: record.status.variant,
      testId: `${testId}-status-${record.id}`,
    },
    meta: [
      ...(showRank ? [{ id: `${record.id}-rank`, label: `#${record.rank}` }] : []),
      ...remainingFields.map((field) => ({
        id: `${record.id}-${field.id}`,
        label: `${field.label}: ${formatDashboardScalar(field.value, field.unit)}`,
      })),
    ],
    leadSentence: record.stateSentence,
    leadTestId: `${testId}-state-${record.id}`,
    valueBlock: {
      signalLabel: leadField.label,
      value: formatDashboardScalar(leadField.value, leadField.unit),
      helper: leadField.detail,
      valueTestId: `${testId}-value-${record.id}`,
    },
    note: recordNote(record),
    noteTestId: `${testId}-reason-${record.id}`,
    ...(readOnly
      ? {}
      : {
          primaryAction: {
            id: record.primaryAction.id,
            label: record.primaryAction.label,
            onClick: () => invokePrimary(record.primaryAction, handlers, record),
            testId: `${testId}-primary-${record.id}`,
          },
        }),
    ...(!readOnly && removeAction
      ? {
          removeAction: {
            id: removeAction.id,
            label: removeAction.label,
            onClick: () => invokePrimary(removeAction, handlers, record),
            testId: `${testId}-remove-${record.id}`,
            confirmTestId: `${testId}-remove-confirm-${record.id}`,
            confirmation: removeAction.confirmation,
            pending: removeAction.pending,
            error: removeAction.error,
          },
        }
      : {}),
    iconActions: [
      ...(!readOnly && secondaryAction
        ? [
            {
              id: secondaryAction.id,
              iconKey: secondaryAction.icon,
              ariaLabel: secondaryAction.label,
              onClick: () => invokePrimary(secondaryAction, handlers, record),
              testId: `${testId}-secondary-${record.id}`,
            },
          ]
        : []),
      {
        id: record.assistantAction.id,
        iconKey: 'assistant' as const,
        ariaLabel: record.assistantAction.label,
        onClick: () => invokeAssistant(record.assistantAction, handlers),
        testId: `${testId}-assistant-${record.id}`,
      },
    ],
    testId: `${testId}-record-${record.id}`,
  };
}

const RankedCollection: React.FC<{
  section: FmDashboardRankedCollectionSection;
  answerState?: FmDashboardState;
  handlers: FmDashboardHandlers;
  testId: string;
  utilityActions?: FmDashboardOverviewProps['headerActions'];
}> = ({ section, answerState, handlers, testId, utilityActions }) => {
  const expertOpen = section.expertTable.open;
  const isEmpty = section.state.status === 'empty';
  const showDistinctState =
    answerState === undefined || !sharesStatePresentation(answerState, section.state);
  return (
    <FmSectionBand
      id={section.id}
      title={isEmpty ? undefined : section.ariaLabel}
      description={isEmpty ? undefined : section.description}
      actions={utilityActions
        ?.filter((action) => action.intent !== 'primary')
        .map((action) => (
          <FmButton
            key={action.id}
            variant="text"
            loading={action.pending}
            onClick={() => invokePrimary(action, handlers)}
            data-testid={`${testId}-header-action-${action.id}`}
          >
            {action.label}
          </FmButton>
        ))}
      tone="light"
      density="compact"
      className="fm-dashboard-overview__ranked-band"
      data-testid={`${testId}-section-${section.id}`}
    >
      <FmStack
        gap="lg"
        role={isEmpty ? 'region' : undefined}
        aria-label={isEmpty ? section.ariaLabel : undefined}
        data-owner-question={section.ownerQuestion}
        data-literal-answer={section.literalAnswer}
      >
        {showDistinctState ? (
          <StateNotice
            state={section.state}
            handlers={handlers}
            testId={`${testId}-${section.id}`}
            renderAction={answerState === undefined}
          />
        ) : null}
        {section.evidence ? (
          <FmEvidence
            evidence={section.evidence}
            data-testid={`${testId}-${section.id}-evidence`}
          />
        ) : null}
        {section.records.length > 0 ? (
          <FmEntityCardGrid
            cards={section.records.map((record) =>
              toEntityCard(
                record,
                handlers,
                testId,
                section.showRank !== false,
                section.state.status === 'locked',
              ),
            )}
            minColumn="lg"
            as="section"
            ariaLabel={section.ariaLabel}
            data-testid={`${testId}-ranked-grid`}
          />
        ) : null}
        {section.records.length > 0 ? (
          <div className="fm-dashboard-overview__expert">
            <FmButton
              variant="text"
              aria-expanded={expertOpen}
              aria-controls={`${testId}-${section.id}-expert-table`}
              onClick={() => handlers.onExpertViewChange(!expertOpen)}
              data-testid={`${testId}-expert-toggle`}
            >
              {expertOpen ? section.expertTable.hideLabel : section.expertTable.showLabel}
            </FmButton>
            {expertOpen ? (
              <div
                id={`${testId}-${section.id}-expert-table`}
                data-testid={`${testId}-expert-table`}
              >
                <FmTable
                  columns={tableColumns(section)}
                  rows={[...section.records]}
                  rowKey={(record) => record.id}
                  caption={section.expertTable.caption}
                  density="compact"
                  mobileLayout="labelled-cells"
                  nonInteractiveHead
                  data-testid={`${testId}-table`}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </FmStack>
    </FmSectionBand>
  );
};

/** Render a validated owner-first dashboard from sealed section descriptors. */
export const FmDashboardOverview: React.FC<FmDashboardOverviewProps> = (props) => {
  validateFmDashboardOverviewProps(props);
  const {
    title,
    subtitle,
    introduction,
    ariaLabel,
    headerActions,
    sections,
    handlers,
    'data-testid': dataTestId = 'fm-dashboard-overview',
  } = props;
  const answer = sections.length === 2 ? sections[0] : undefined;
  const collection = sections.length === 2 ? sections[1] : sections[0];
  const topRecord =
    answer === undefined || answer.topRecordId === null
      ? undefined
      : collection.records.find((record) => record.id === answer.topRecordId);
  const rankingRunId = answer?.rankingRunId ?? collection.rankingRunId;
  return (
    <FmRoutePage
      width="flush"
      fit="content"
      className="fm-dashboard-overview"
      aria-label={ariaLabel}
      data-testid={dataTestId}
      data-ranking-run-id={rankingRunId ?? undefined}
    >
      <DashboardHeader
        title={title}
        subtitle={subtitle}
        introduction={introduction}
        headerActions={headerActions}
        handlers={handlers}
        testId={dataTestId}
      />
      {answer ? (
        <CalmAnswer
          section={answer}
          topRecord={topRecord}
          handlers={handlers}
          testId={dataTestId}
        />
      ) : null}
      <RankedCollection
        section={collection}
        answerState={answer?.state}
        handlers={handlers}
        testId={dataTestId}
        utilityActions={introduction ? headerActions : undefined}
      />
    </FmRoutePage>
  );
};

FmDashboardOverview.displayName = 'FmDashboardOverview';
export default FmDashboardOverview;
